import React, { FC, useCallback, useEffect, useState } from 'react';

import CollapsibleArea from '@/components/CollapsibleArea';
import { Table, TableRow, TD } from '@/components/DesignLibrary/Table';
import getNormalisedCurrencyValue from '@/helpers/getNormalisedCurrencyValue';
import { Currency } from '@/typings/api-models';
import NoodleAI from '@/components/Icons/NoodleAI';
import { MathOperations } from '@phosphor-icons/react';
import ProgressIndicator from '@/components/ProgressIndicator';

import s from './DocumentRequestUserDetail.module.scss';

export type SchedulesIResponse = {
  grossPay: number;
  overTimePay: number;
  grossMonthlyIncome: number;
  taxMedicareAndSocialSecurityDeductions: number;
  mandatoryContributionsForRetirementPlans: number;
  voluntaryContributionsForRetirementPlans: number;
  requiredRepaymentsOfRetirementFundLoans: number;
  insurance: number;
  domesticSupportObligations: number;
  unionDues: number;
  otherDeductions: number;
  netPay: number;
  rentalPropertyIncome: number;
  interestAndDividends: number;
  familySupportReceived: number;
  unemploymentCompensations: number;
  socialSecurityIncome: number;
  otherGovernmentAssistance: number;
  pensionOrRetirementIncome: number;
  freeFormOtherIncome: number;
  totalDeductions: number | null;
  totalIncome: number | null;
  incomeAfterDeductions: number | null;
};

const scheduleIDataKeys = [
  {
    key: 'grossMonthlyIncome',
    label: "2. Monthly gross wages, salary, and commissions",
  },
  {
    key: 'overtimePerPayPeriod',
    label: "3. Overtime pay",
  },
  {
    isCalculation: true,
    key: 'grossSalary',
    label: "4. Gross Income",
  },
  {
    key: 'taxMedicareAndSocialSecurityDeductions',
    label: "5a. Tax, Medicare, and Social Security Deductions",
  },
  {
    key: 'mandatoryContributionsForRetirementPlans',
    label: "5b. Mandatory contributions for retirement plans",
  },
  {
    key: 'voluntaryContributionsForRetirementPlans',
    label: "5c. Voluntary contributions for retirement plans",
  },
  {
    key: 'requiredRepaymentsOfRetirementFundLoans',
    label: "5d. Required repayments of retirement fund loans",
  },
  {
    key: 'insurance',
    label: "5e. Insurance",
  },
  {
    key: 'domesticSupportObligations',
    label: "5f. Domestic support obligations",
  },
  {
    key: 'unionDues',
    label: "5g. Union dues",
  },
  {
    key: 'otherDeductions',
    label: "5h. Other deductions",
  },
  {
    isCalculation: true,
    key: 'totalDeductions',
    label: "6. Total Payroll Deductions",
  },
  {
    isCalculation: true,
    key: 'takeHomeSalary',
    label: "7. Total monthly take-home pay",
  },
  {
    key: 'netBusinessRentalIncome',
    label: "8a. Net income from rental property and from operating a business, profession, or a farm",
  },
  {
    key: 'interestAndDividends',
    label: "8b. Interest and dividends",
  },
  {
    key: 'familySupportReceived',
    label: "8c. Family support payments that you, a non-filing spouse, or a dependent regularly receive",
  },
  {
    key: 'unemploymentCompensations',
    label: "8d. Unemployment Compensation",
  },
  {
    key: 'socialSecurityIncome',
    label: "8e. Social Security",
  },
  {
    key: 'otherGovernmentAssistance',
    label: "8f. Other government assistance that you regularly receive",
  },
  {
    key: 'pensionOrRetirementIncome',
    label: "8g. Pension or retirement income",
  },
  {
    key: 'freeFormOtherIncome',
    label: "8h. Other monthly income",
  },
  {
    isCalculation: true,
    key: 'totalOtherIncome',
    label: "9. Total other income",
  },
  {
    isCalculation: true,
    key: 'monthlyIncome',
    label: "10. Monthly income",
  },
];

type Props = {
  data: SchedulesIResponse;
  fetchingData: boolean;
};

const SchedulesICalculator: FC<Props> = ({ data, fetchingData }) => {
  const [localData, setLocalData] = useState<SchedulesIResponse | null>(null);

  useEffect(() => {
    if (data) {
      setLocalData(data);
    }
  }, [data]);

  const normalizeValue = useCallback((value: string | number | null | undefined): string => getNormalisedCurrencyValue(value, Currency.Usd), []);

  if (!localData) {
    return null;
  }

  return (
    <CollapsibleArea
      title="Schedule I Calculator"
      description="These values will sync automatically to the Bankruptcy Schedules. Changes in this table will reflect in Schedule I in real-time for this debtor."
      CustomIcon={fetchingData ? <ProgressIndicator /> : <NoodleAI />}
      className={s.calculatorSection}
      caretPosition='right'
    >
      <div className={s.calculatorTable}>
        <Table>
          {scheduleIDataKeys.map(({ key, label, isCalculation }) => (
            <TableRow key={key} state={isCalculation ? 'note' : null}>
              {isCalculation
                ? <TD compact>
                  <div className={s.calculationCell}><MathOperations />{label}</div>
                </TD>
                : <TD compact label={label} />}
              <TD compact label={normalizeValue(localData[key as keyof SchedulesIResponse])} />
            </TableRow>
          ))}
        </Table>
      </div>
    </CollapsibleArea>
  );
};

export default SchedulesICalculator;
